import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MessageSquare, Send } from 'lucide-react';
import emailjs from '@emailjs/browser';
import axios from 'axios';
import { trackEvent, trackStep } from '../../lib/track';
import { CONTACT } from '../../mock';
import { useToast } from '../../hooks/use-toast';
import { MaskedLines, Reveal } from './reveal';

export default function HammerContact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    smsConsent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Funnel: fire form_start once, on first field interaction
  const formStarted = useRef(false);
  const onFirstFocus = () => {
    if (!formStarted.current) {
      formStarted.current = true;
      trackStep('form_start');
    }
  };

  // Shipping estimator handoff: "Add to my quote" drops the estimate text
  // into the message box, scrolls the form into view, and focuses the textarea.
  const messageRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      const msg = (e.detail && e.detail.message) || '';
      if (!msg) return;
      setForm((f) => ({ ...f, message: msg }));
      const formEl = document.getElementById('hammer-quote');
      if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        if (messageRef.current) messageRef.current.focus({ preventScroll: true });
      }, 600);
    };
    window.addEventListener('prefill-quote-message', handler);
    return () => window.removeEventListener('prefill-quote-message', handler);
  }, []);

  const phoneLines = useMemo(
    () => CONTACT.phones.map((p) => `${p.number} — ${p.name}`),
    []
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast({
        title: 'Missing details',
        description: 'Name, phone, and email are all required so we can respond to you.',
      });
      return;
    }
    if (form.message.trim().length < 4) {
      toast({
        title: 'Message too short',
        description: 'Please add a few words about your inquiry (at least 4 characters).',
      });
      return;
    }
    if (!form.smsConsent) {
      toast({
        title: 'One more step',
        description: 'Please check the box to consent to texts/calls so we can respond to your inquiry.',
      });
      return;
    }
    setSubmitting(true);
    // Save the lead to the backend DB in parallel with the EmailJS send.
    // Errors are swallowed so a DB hiccup can't break the email flow.
    axios
      .post(`${process.env.REACT_APP_LEADS_API_URL}/api/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        message: form.message.trim(),
        source: 'hammer',
        sms_consent: form.smsConsent,
      })
      .catch(() => {});
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        // Falls back to the barge template if the hammer-specific one isn't
        // provisioned yet — keeps submissions working during the switchover.
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID_HAMMER ||
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: form.name,
          phone: form.phone,
          email: form.email,
          // Prefix the message so the shared owner inbox knows this is a
          // hammer-source lead.
          message: `[HAMMER LEAD] ${form.message.trim()}`,
          sms_consent: form.smsConsent
            ? 'YES — checked SMS consent on absolutehammers.com contact form'
            : 'NO',
          time: new Date().toLocaleString('en-US', {
            timeZone: 'America/New_York',
            dateStyle: 'medium',
            timeStyle: 'short',
          }),
        },
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      toast({
        title: 'Message sent',
        description: "We'll reach out within one business day.",
      });
      trackEvent('lead_submit', {
        source: 'hammer',
        name: form.name,
        phone: form.phone,
        email: form.email,
      });
      setForm({ name: '', phone: '', email: '', message: '', smsConsent: false });
    } catch (err) {
      toast({
        title: "Couldn't send message",
        description:
          err && (err.text || err.message)
            ? `${err.text || err.message}. If this persists, call ${CONTACT.salesPhone.number}.`
            : `Please try again or call ${CONTACT.salesPhone.number} directly.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="hammer-contact"
      data-testid="hammer-contact-section"
      className="relative py-16 sm:py-20 lg:py-28 bg-black border-t border-white/5"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <span className="h-px w-8 sm:w-10 bg-cyan-400" />
              <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
                Order · Questions · Freight
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl font-bold uppercase leading-[1.02] tracking-tight">
            <MaskedLines
              delay={0.15}
              lines={[
                'Get your',
                <span className="text-cyan-300">Absolute Hammer.</span>,
              ]}
            />
          </h2>
          <Reveal delay={0.3}>
            <p className="mt-5 text-zinc-400 text-sm sm:text-base md:text-lg leading-relaxed">
              Tell us your ship-to and we'll come back with a firm freight quote,
              estimated delivery date, and payment instructions. We answer every
              inquiry within one business day.
            </p>
          </Reveal>
        </div>

        <div className="mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10">
          {/* Contact rail */}
          <Reveal delay={0.2} className="space-y-6">
            <div className="panel rounded-xl p-5 sm:p-6">
              <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500 mb-3">
                Direct Lines
              </div>
              <ul className="space-y-2 text-zinc-200 text-sm sm:text-base">
                {phoneLines.map((l) => (
                  <li key={l} className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-cyan-300" />
                    {l}
                  </li>
                ))}
              </ul>
            </div>

            <div className="panel rounded-xl p-5 sm:p-6">
              <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500 mb-3">
                Email
              </div>
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-2 text-zinc-200 hover:text-cyan-300 transition-colors break-all"
              >
                <Mail className="w-4 h-4 text-cyan-300 shrink-0" /> {CONTACT.email}
              </a>
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href={`tel:${CONTACT.salesPhone.tel}`}
                onClick={() => trackEvent('contact_click', { method: 'call' })}
                data-testid="hammer-contact-call-btn"
                className="btn-teal px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
              >
                <Phone className="w-4 h-4" /> Call {CONTACT.salesPhone.number}
              </a>
              <a
                href={`sms:${CONTACT.salesPhone.tel}`}
                onClick={() => trackEvent('contact_click', { method: 'sms' })}
                data-testid="hammer-contact-sms-btn"
                className="btn-ghost px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" /> Text us
              </a>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.35}>
          <form
            id="hammer-quote"
            onSubmit={submit}
            className="panel rounded-xl p-6 sm:p-7 lg:p-9 scroll-mt-24"
            data-testid="hammer-quote-form"
          >
            <h3 className="font-display text-white text-xl sm:text-2xl font-semibold tracking-wide">
              Request a Hammer
            </h3>
            <p className="text-zinc-500 text-sm mt-1">
              $10,500* · we respond within one business day.
            </p>

            <div className="mt-7 space-y-5">
              <Field label="Your Name" req>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  onFocus={onFirstFocus}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="John Doe"
                  data-testid="hammer-form-name"
                />
              </Field>
              <Field label="Phone" req>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="(321) 000-0000"
                  data-testid="hammer-form-phone"
                />
              </Field>
              <Field label="Email" req>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white"
                  placeholder="you@example.com"
                  data-testid="hammer-form-email"
                />
              </Field>
              <Field label="Message" req>
                <textarea
                  required
                  minLength={4}
                  ref={messageRef}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none rounded-md px-4 py-3 text-white resize-none"
                  placeholder="How many hammers, ship-to city / state, and any questions."
                  data-testid="hammer-form-message"
                />
              </Field>

              {/* Honeypot */}
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                style={{ display: 'none' }}
              />

              <label
                className="flex gap-3 items-start cursor-pointer group"
                data-testid="hammer-sms-consent-block"
              >
                <input
                  type="checkbox"
                  required
                  checked={form.smsConsent}
                  onChange={(e) => setForm({ ...form, smsConsent: e.target.checked })}
                  className="mt-1 w-4 h-4 shrink-0 accent-cyan-400 cursor-pointer"
                  data-testid="hammer-sms-consent-checkbox"
                />
                <span className="text-[12px] leading-relaxed text-zinc-400 group-hover:text-zinc-300 transition-colors">
                  <span className="text-cyan-300">* </span>By checking this box and submitting this form, I consent to receive calls and SMS/MMS text messages from{' '}
                  <span className="text-zinc-200">Absolute Marine Equipment</span> at the phone number provided, related to my inquiry, quote, order, delivery, and future offers. Checking this box is required to send an inquiry. Message frequency varies. Message &amp; data rates may apply. Reply{' '}
                  <span className="text-zinc-200">STOP</span> to opt out or{' '}
                  <span className="text-zinc-200">HELP</span> for help. See our{' '}
                  <Link to="/privacy" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/sms-terms" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">
                    SMS Terms
                  </Link>
                  .
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="hammer-form-submit"
                className="btn-teal w-full px-5 py-3 rounded-md inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.18em] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Send Message'}
              </button>
            </div>
          </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, req }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
        {label}
        {req && <span className="text-cyan-300"> *</span>}
      </div>
      {children}
    </label>
  );
}
